import { logError, ERROR_TYPE } from "../newRelicLogger";
import getWindow from "../window";
import getRuntimeConstants from "../../runtimeConstants";
import { buildStructuredDataDocument } from "../../../common/structuredData/buildStructuredDataDocument";
import { getProductUrl } from "../url";
import type {
  StructuredDataInputRatings,
  StructuredDataInputFacetGroup,
  JsonLdDocument,
} from "../../../common/structuredData/types";

interface FacetProduct {
  productId: number;
  brandName: string;
  name: string;
  isSelected: boolean;
}

interface FacetGroup {
  facetGroupId: string | number;
  facets?: Array<{ type: string; products?: FacetProduct[] }> | null;
}

interface ClientVariant {
  variantId: number | string;
  price: { current: { value: number }; currency: string };
  isInStock: boolean;
  [key: string]: unknown;
}

interface ClientProducts {
  variants: ClientVariant[];
  [key: string]: unknown;
}

export interface ClientSchemaInputs {
  products: ClientProducts;
  ratings?: StructuredDataInputRatings | null;
}

function getColourVariantUrls(facetGroup: FacetGroup | null): string[] {
  const supplierColourFacet = facetGroup?.facets?.find(
    (f) => f.type === "SupplierColour"
  );
  return (
    supplierColourFacet?.products
      ?.filter((p) => !p.isSelected)
      .map((p) => getProductUrl(p.brandName, p.name, p.productId)) ?? []
  );
}

export function createAndInsertInToPage({
  products,
  ratings,
}: ClientSchemaInputs): void {
  try {
    const runtimeConstants = getRuntimeConstants();
    const { product, canonicalUrl, productDescription } = runtimeConstants;

    const stockPrice = products.variants.map((v) => ({
      ...v,
      id: v.variantId,
    }));

    const rawFacetGroup = (product.facetGroup as FacetGroup) ?? null;
    const colourVariantUrls = getColourVariantUrls(rawFacetGroup);
    const facetGroup: StructuredDataInputFacetGroup | null = rawFacetGroup
      ? { ...rawFacetGroup, colourVariantUrls }
      : null;

    const jsonLdDocument = buildStructuredDataDocument(
      {
        product,
        productDescription: productDescription?.productDescription ?? null,
        canonicalUrl,
        facetGroup,
        stockPrice,
        ratings,
      },
      product.media
    );

    if (jsonLdDocument) {
      insertStructuredDataScript(jsonLdDocument);
    }
  } catch (error) {
    logError(ERROR_TYPE.NonCriticalAppError, {
      action: "RenderingStructuredDataToPage",
      message: error instanceof Error ? error.message : String(error),
    });
  }
}

function insertStructuredDataScript(jsonLdDocument: JsonLdDocument): void {
  const element = getWindow().document.getElementById("split-structured-data");
  if (element) {
    element.textContent = JSON.stringify(jsonLdDocument);
  }
}
